/**
 * Settings → Texting: what happens when someone texts the business, in plain
 * words, with the automatic texts an owner can reword right on the row.
 *
 * Johnny (2026-10-30): whether clients get AI replies is the owner's call in
 * the platform, with a customizable message otherwise. The switch itself
 * stays on Settings → AI Assistant (owner-only): this card links to it and
 * never duplicates it.
 *
 * The preview renders through `text-framing.ts`, the same functions the server
 * sends through, so what it shows is what a phone gets. The business name, the
 * STOP line and the sign-up link are added there, outside the owner's words,
 * which is why they cannot be edited away.
 *
 * Design Philosophy principles served:
 *  - #1 One glance, one truth: the top line says whether texting is live; each
 *    row is one situation and exactly what happens.
 *  - #2 The next action is obvious: while it isn't live, the card lists what's
 *    left and who does it, so saving a message is never a silent dead end.
 *  - #5 Never lie about data: "No automatic reply" is said outright; standard
 *    wording is shown exactly as sent; a message that isn't live yet says so.
 *  - #7 Built for hands: full-size buttons and a phone-width editor.
 *  - Five states: loading (skeleton) · error (+ Try again) · empty (no message
 *    saved: says no reply goes out) · ready · saving/save error keeping the
 *    typed words. Stale doesn't apply: the card reads live state on open.
 *
 * Counter Test (owner altitude): Ted opens Texting, reads "Texting isn't live
 * yet" and the one step left, sees what a client gets with Woofers off, taps
 * Edit, types, watches the text and its length update, taps Save.
 *
 * Frontends go live on merge, the server on a manual deploy. On a server that
 * predates the route (403 route_not_mapped) this renders NOTHING rather than
 * a Try again that can never succeed (the WordingSettingsCard rule).
 *
 * Server contract: GET/PUT /api/v2/admin/tenant/messaging/auto-replies.
 */
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Alert, Badge, Button, Card, Skeleton } from '@ferro/ui';
import { clientTextBody, smsSegments, unknownSenderCustomText } from '@ferro/core/modules/messaging-sms/text-framing.js';
import { adminMessagingSettingsApi, type AutoRepliesSettings, type TextingMessageKey } from '../../lib/api.js';
import { whenLabel } from './last-change-label.js';
import './TextingMessagesCard.css';

const TITLE = 'When someone texts your number';

interface TextingMessagesCardProps {
  apiUrl: string;
  /** Holds business-config:write:all. Everyone else reads. */
  canEdit: boolean;
  /** Can use the Twilio card on this page. */
  canManageTwilio: boolean;
}

type Load = { kind: 'loading' } | { kind: 'not_deployed' } | { kind: 'error'; message: string } | { kind: 'ready'; view: AutoRepliesSettings };

type Editor = { key: TextingMessageKey; draft: string; saving: boolean; error: string | null } | null;

/** Names for the last-change line. */
const CHANGED_LABELS: Record<TextingMessageKey, string> = {
  assistantOff: 'the reply when the assistant is off',
  assistantUnavailable: "the reply when the assistant can't answer",
  clientLimit: 'the reply when a client sends too many texts',
  unknownSender: "the reply to numbers that aren't clients",
};

function humanList(items: readonly string[]): string {
  if (items.length <= 1) return items[0] ?? '';
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
}

/** The length guidance under a preview, in an owner's words. */
export function lengthGuidance(body: string): string {
  const s = smsSegments(body);
  const size = s.characters === 1 ? '1 character' : `${s.characters} characters`;
  const count = s.segments <= 1 ? 'fits in 1 text' : `goes out as ${s.segments} texts, and each one is billed`;
  if (s.encoding === 'gsm7') return `${size}, ${count}.`;
  const chars = s.specialCharacters.join(' ');
  return `${size}, ${count}. ${chars} shrinks each text to ${s.perSegment} characters from ${s.perSegment === 70 ? 160 : 153}.`;
}

function TextBubble({ body, testId }: { body: string; testId?: string }): JSX.Element {
  return (
    <p className="ferro-texting__bubble" data-testid={testId}>
      {body}
    </p>
  );
}

function Preview({ label, body, testId }: { label: string; body: string; testId: string }): JSX.Element {
  return (
    <div className="ferro-texting__preview">
      <p className="ferro-texting__preview-label">{label}</p>
      <TextBubble body={body} testId={testId} />
      <p className="ferro-field__helper" data-testid={`${testId}-length`}>
        {lengthGuidance(body)}
      </p>
    </div>
  );
}

/** A situation with no wording to edit. */
function FixedRow({ title, children, testId, tag }: { title: string; children: string; testId: string; tag?: JSX.Element | null }): JSX.Element {
  return (
    <li className="ferro-texting__row" data-testid={testId}>
      <div className="ferro-texting__row-head">
        <h3 className="ferro-settings__subheading">{title}</h3>
        {tag}
      </div>
      <p className="ferro-texting__what">{children}</p>
    </li>
  );
}

export function TextingMessagesCard({ apiUrl, canEdit, canManageTwilio }: TextingMessagesCardProps): JSX.Element | null {
  const [load, setLoad] = useState<Load>({ kind: 'loading' });
  const [editor, setEditor] = useState<Editor>(null);
  const [savedKey, setSavedKey] = useState<TextingMessageKey | null>(null);

  const refresh = useCallback(async () => {
    setLoad({ kind: 'loading' });
    const res = await adminMessagingSettingsApi.getAutoReplies(apiUrl);
    if (res.reason === 'route_not_mapped') {
      setLoad({ kind: 'not_deployed' });
      return;
    }
    if (res.error || !res.data) {
      setLoad({ kind: 'error', message: res.error ?? 'Could not load these messages.' });
      return;
    }
    setLoad({ kind: 'ready', view: res.data });
  }, [apiUrl]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const save = useCallback(
    async (key: TextingMessageKey, value: string | null, draft: string) => {
      setSavedKey(null);
      setEditor({ key, draft, saving: true, error: null });
      const res = await adminMessagingSettingsApi.updateAutoReplies(apiUrl, { [key]: value });
      if (res.error || !res.data) {
        const detail = Array.isArray(res.details) ? (res.details[0] as { message?: unknown } | undefined)?.message : undefined;
        setEditor({
          key,
          draft,
          saving: false,
          error: typeof detail === 'string' ? detail : (res.error ?? 'Could not save that message. Please try again.'),
        });
        return;
      }
      const { changedKeys: _changedKeys, audited: _audited, ...view } = res.data;
      setLoad({ kind: 'ready', view });
      setEditor(null);
      setSavedKey(key);
    },
    [apiUrl],
  );

  if (load.kind === 'not_deployed') return null;
  if (load.kind === 'loading') {
    return (
      <Card title={TITLE}>
        <div data-testid="texting-messages-loading">
          <Skeleton variant="text" lines={5} />
        </div>
      </Card>
    );
  }
  if (load.kind === 'error') {
    return (
      <Card title={TITLE}>
        <div data-testid="texting-messages-error">
          <Alert variant="error">{load.message}</Alert>
          <div className="ferro-settings__actions">
            <Button variant="outline" onClick={() => void refresh()} data-testid="texting-messages-retry">
              Try again
            </Button>
          </div>
        </div>
      </Card>
    );
  }

  const { view } = load;
  const { messages, framing, assistant, readiness, limits, lastChange } = view;
  const name = assistant.name;
  const whoStart = name ?? 'Your assistant';
  const who = name ?? 'your assistant';
  const live = readiness.twilioConnected && readiness.programOn;
  const current = <Badge variant="success">How it&apos;s set now</Badge>;
  const alertLine = readiness.alertNumberSet
    ? 'Either way, you get a short text at your alert number.'
    : 'You have no alert number set, so nobody is told.';

  const editableRow = (key: TextingMessageKey, title: string, what: string, tag?: JSX.Element | null): JSX.Element => {
    const message = messages[key];
    const open = editor?.key === key ? editor : null;
    const draft = open?.draft ?? '';
    const trimmed = draft.trim();
    const tooLong = trimmed.length > limits.maxLength;
    const stranger = key === 'unknownSender';
    const noneByDefault = message.standard.length === 0;
    const setDraft = (next: string) => setEditor(open ? { ...open, draft: next, error: null } : null);

    return (
      <li className="ferro-texting__row" data-testid={`texting-row-${key}`}>
        <div className="ferro-texting__row-head">
          <h3 className="ferro-settings__subheading">{title}</h3>
          {tag}
        </div>
        <p className="ferro-texting__what">{what}</p>

        {open === null ? (
          <>
            {message.custom !== null ? (
              <div className="ferro-texting__words">
                <Badge variant="neutral">Your wording</Badge>
                <TextBubble body={message.custom} testId={`texting-custom-${key}`} />
              </div>
            ) : noneByDefault ? (
              <p className="ferro-texting__none" data-testid={`texting-none-${key}`}>
                No automatic reply. Your team answers from the Inbox.
              </p>
            ) : (
              <div className="ferro-texting__words">
                <Badge variant="platform">Standard wording</Badge>
                {message.standard.length > 1 ? (
                  <p className="ferro-field__helper">Clients get one of these, depending on what happened:</p>
                ) : null}
                {message.standard.map((text) => (
                  <TextBubble key={text} body={text} testId={`texting-standard-${key}`} />
                ))}
              </div>
            )}
            {savedKey === key ? (
              <div data-testid={`texting-saved-${key}`}>
                <Alert variant="success">
                  {live ? 'Saved. Clients get this from now on.' : 'Saved. It starts going out once texting is live.'}
                </Alert>
              </div>
            ) : null}
            {canEdit ? (
              <div className="ferro-settings__actions">
                <Button
                  type="button"
                  variant="outline"
                  disabled={editor !== null}
                  onClick={() => {
                    setSavedKey(null);
                    setEditor({ key, draft: message.custom ?? message.startingPoint, saving: false, error: null });
                  }}
                  data-testid={`texting-edit-${key}`}
                >
                  {message.custom === null && noneByDefault ? 'Write a reply' : 'Edit'}
                </Button>
              </div>
            ) : null}
          </>
        ) : (
          <form
            className="ferro-form ferro-settings__form"
            noValidate
            aria-label={title}
            onSubmit={(e) => {
              e.preventDefault();
              if (trimmed !== '' && !tooLong && !open.saving) void save(key, trimmed, draft);
            }}
          >
            <div className="ferro-field">
              <label htmlFor={`texting-draft-${key}`} className="ferro-field__label">
                Your words
              </label>
              <textarea
                id={`texting-draft-${key}`}
                className="ferro-field__input ferro-input ferro-texting__textarea"
                rows={4}
                value={draft}
                disabled={open.saving}
                aria-invalid={tooLong || open.error !== null ? true : undefined}
                onChange={(e) => setDraft(e.target.value)}
                data-testid={`texting-draft-${key}`}
              />
              <p className="ferro-field__helper" data-testid={`texting-counter-${key}`}>
                {trimmed.length} / {limits.maxLength} characters. One text holds 160.
              </p>
              {tooLong ? (
                <p className="ferro-form__error" role="alert">
                  Keep it to {limits.maxLength} characters or fewer.
                </p>
              ) : null}
            </div>

            {trimmed !== '' ? (
              <div className="ferro-texting__previews">
                {stranger ? (
                  <Preview
                    label="What they get"
                    body={unknownSenderCustomText({ content: trimmed, businessName: framing.businessName, signUpUrl: framing.signUpUrl })}
                    testId={`texting-preview-${key}`}
                  />
                ) : (
                  <>
                    <Preview
                      label="A client's first text from you"
                      body={clientTextBody({ content: trimmed, firstText: true, tenantName: framing.businessName })}
                      testId={`texting-preview-first-${key}`}
                    />
                    <Preview
                      label="After that"
                      body={clientTextBody({ content: trimmed, firstText: false, tenantName: framing.businessName })}
                      testId={`texting-preview-later-${key}`}
                    />
                  </>
                )}
                <p className="ferro-field__helper" data-testid={`texting-locked-${key}`}>
                  {stranger
                    ? 'Your business name, the sign-up link and "Reply STOP to opt out." are always added. Carriers require the opt-out, so it can\'t be removed.'
                    : 'Your business name and "Reply STOP to opt out." are added to a first text. Carriers require the opt-out, so it can\'t be removed.'}
                </p>
              </div>
            ) : null}

            {open.error ? (
              <div data-testid={`texting-save-error-${key}`}>
                <Alert variant="error">{open.error}</Alert>
              </div>
            ) : null}

            <div className="ferro-settings__actions">
              <Button
                type="submit"
                variant="primary"
                isLoading={open.saving}
                disabled={trimmed === '' || tooLong || open.saving}
                data-testid={`texting-save-${key}`}
              >
                Save
              </Button>
              <Button type="button" variant="ghost" disabled={open.saving} onClick={() => setEditor(null)} data-testid={`texting-cancel-${key}`}>
                Cancel
              </Button>
              {message.custom !== null ? (
                <Button
                  type="button"
                  variant="ghost"
                  disabled={open.saving}
                  onClick={() => void save(key, null, draft)}
                  data-testid={`texting-reset-${key}`}
                >
                  {noneByDefault ? 'Stop sending a reply' : 'Go back to the standard wording'}
                </Button>
              ) : null}
            </div>
          </form>
        )}
      </li>
    );
  };

  return (
    <Card title={TITLE} subtitle="What clients get back, in plain words. Nothing here depends on the time of day.">
      <div className="ferro-settings__card" data-testid="texting-messages-ready">
        {live ? (
          <div data-testid="texting-live">
            <Alert variant="success">Texting is live. Clients who text your number get what you see below.</Alert>
          </div>
        ) : (
          <div className="ferro-texting__status" data-testid="texting-not-live">
            <Alert variant="warning">
              Texting isn&apos;t live yet, so no automatic text goes out. Anything you save here is ready for the day it does.
            </Alert>
            <ol className="ferro-texting__steps">
              <li className="ferro-texting__step" data-testid="texting-step-twilio">
                <Badge variant={readiness.twilioConnected ? 'success' : 'warning'}>{readiness.twilioConnected ? 'Done' : 'To do'}</Badge>
                <span>
                  <strong>Connect your Twilio account.</strong>{' '}
                  {readiness.twilioConnected
                    ? 'Connected.'
                    : canManageTwilio
                      ? 'Use the Twilio card on this page.'
                      : 'Your business owner does this on this page.'}
                </span>
              </li>
              <li className="ferro-texting__step" data-testid="texting-step-program">
                <Badge variant={readiness.programOn ? 'success' : 'warning'}>{readiness.programOn ? 'Done' : 'Waiting'}</Badge>
                <span>
                  <strong>Carrier approval, then texting turned on for your business.</strong>{' '}
                  {readiness.programOn
                    ? 'Turned on.'
                    : 'Animal Lovers App turns this on once the carriers approve your number. There is nothing for you to do here.'}
                </span>
              </li>
            </ol>
          </div>
        )}

        <p className="ferro-texting__who" data-testid="texting-who-answers">
          <strong>
            {whoStart} answers texts: {assistant.textingEnabled ? 'On' : 'Off'}.
          </strong>{' '}
          <Link to="/settings/assistant" data-testid="texting-assistant-link">
            Turn it on or off in AI Assistant
          </Link>{' '}
          (owners only).
        </p>

        {lastChange ? (
          <p className="ferro-settings__last-change" data-testid="texting-last-change">
            Last changed by <strong>{lastChange.byName}</strong>
            {lastChange.at ? ` on ${whenLabel(lastChange.at)}` : ''}
            {lastChange.changedKeys.length > 0 ? `: ${humanList(lastChange.changedKeys.map((k) => CHANGED_LABELS[k]))}` : ''}
          </p>
        ) : null}

        {!canEdit ? (
          <p className="ferro-muted ferro-settings__readonly-hint" data-testid="texting-readonly">
            You can read these messages. Your role can&apos;t change them.
          </p>
        ) : null}

        <ul className="ferro-texting__rows">
          {editableRow(
            'assistantOff',
            `When ${who} is off`,
            'A client\'s text goes to your Inbox. Once a day at most, they get the reply below, if you write one.',
            assistant.textingEnabled ? null : current,
          )}
          <FixedRow title={`When ${who} is on`} testId="texting-row-assistant-on" tag={assistant.textingEnabled ? current : null}>
            {`${whoStart} writes each reply itself from the client's bookings, pets and your prices. Anything it can't do goes to your team, and you get one short text.`}
          </FixedRow>
          {editableRow(
            'assistantUnavailable',
            `When ${who} can't answer`,
            `It failed, it's offline, it couldn't finish, or it's at its hourly limit. The client gets the reply below and the conversation goes to your team.`,
          )}
          {editableRow(
            'clientLimit',
            'When one client sends too many texts',
            `${whoStart} pauses for that client for the hour and tells them once. Your team takes it from there.`,
          )}
          {editableRow(
            'unknownSender',
            "When a number that isn't a client texts",
            `While ${who} is on, they get the reply below once a day. While it's off, they get no reply. ${alertLine}`,
          )}
          <FixedRow title="When your team takes over a conversation" testId="texting-row-staff">
            No automatic replies go to that client until your team hands the conversation back.
          </FixedRow>
          <FixedRow title="When a client replies STOP" testId="texting-row-stop">
            Twilio and the carriers send the required reply and stop all texts to that number, and we record it. This can&apos;t be changed.
          </FixedRow>
          <FixedRow title="When a client replies HELP" testId="texting-row-help">
            Twilio and the carriers send the required reply. It goes to your Inbox too, and nothing else answers it. This can&apos;t be changed.
          </FixedRow>
          <FixedRow title="After hours" testId="texting-row-after-hours">
            The same as any other time. There are no separate after-hours texts.
          </FixedRow>
        </ul>
      </div>
    </Card>
  );
}
